import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatError } from "@/components/shared/helpers";
import { FileSpreadsheet, FileText, Calendar } from "lucide-react";

const ENTITIES = [
  { value: "ALL", label: "Toutes entités", color: "slate" },
  { value: "BDM", label: "Blanc des Montagnes", color: "violet" },
  { value: "BDM_PARTICULIER", label: "BDM Particulier", color: "fuchsia" },
];

const today = () => new Date().toISOString().slice(0, 10);
const startOfYear = () => `${new Date().getFullYear()}-01-01`;

export default function AccountingExport() {
  const [entity, setEntity] = useState("ALL");
  const [fromDate, setFromDate] = useState(startOfYear());
  const [toDate, setToDate] = useState(today());
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const setPresetMonth = (offset = 0) => {
    const d = new Date();
    d.setMonth(d.getMonth() + offset);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const lastDay = new Date(y, d.getMonth() + 1, 0).getDate();
    setFromDate(`${y}-${m}-01`);
    setToDate(`${y}-${m}-${String(lastDay).padStart(2, "0")}`);
  };

  const setPresetYear = () => {
    setFromDate(startOfYear());
    setToDate(today());
  };

  const loadPreview = async () => {
    if (!fromDate || !toDate) {
      toast({ title: "Période manquante", variant: "destructive" });
      return;
    }
    try {
      setBusy(true);
      const res = await api.get("/admin/invoices", { params: {} });
      const list = (res.data || []).filter((inv) => {
        if (entity !== "ALL" && (inv.entity || "BDM") !== entity) return false;
        const d = (inv.issue_date || "").slice(0, 10);
        return d >= fromDate && d <= toDate;
      });
      const totalTtc = list.reduce((acc, i) => acc + (Number(i.amount_ttc) || 0), 0);
      const paidTtc = list.reduce((acc, i) => acc + (i.paid ? (Number(i.amount_ttc) || 0) : 0), 0);
      setPreview({ count: list.length, total_ttc: totalTtc, paid_ttc: paidTtc, unpaid_ttc: totalTtc - paidTtc });
    } catch (e) {
      toast({ title: "Erreur prévisualisation", description: formatError(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, fromDate, toDate]);

  const download = async (format) => {
    if (!fromDate || !toDate) {
      toast({ title: "Période manquante", variant: "destructive" });
      return;
    }
    try {
      setBusy(true);
      const res = await api.get(`/admin/exports/accounting/${format}`, {
        params: { entity, from_date: fromDate, to_date: toDate },
        responseType: "blob",
      });
      const mime = format === "csv" ? "text/csv" : "application/pdf";
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `comptabilite_${entity}_${fromDate}_au_${toDate}.${format}`;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast({ title: `✅ Export ${format.toUpperCase()} téléchargé`, description: `${preview?.count || 0} facture(s) · ${(preview?.total_ttc || 0).toFixed(2)} € TTC` });
    } catch (e) {
      toast({ title: "Erreur export", description: formatError(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="accounting-export-card" className="border-white/10 bg-white/[0.08]">
      <CardHeader>
        <CardTitle className="text-slate-50 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-emerald-300" />
          Export comptable — Livre des recettes
        </CardTitle>
        <div className="text-sm text-slate-300">
          Génère un export CSV (compatible Excel / expert-comptable) ou un récapitulatif PDF des factures émises sur une période. Filtrable par entité (BDM ou BDM Particulier).
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filtres */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="space-y-1">
            <Label className="text-slate-200">Entité</Label>
            <select
              data-testid="accounting-export-entity"
              className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
              value={entity}
              onChange={(e) => setEntity(e.target.value)}
            >
              {ENTITIES.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-slate-200">Du</Label>
            <Input data-testid="accounting-export-from" type="date" className="border-white/10 bg-white/[0.08] text-slate-50" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-slate-200">Au</Label>
            <Input data-testid="accounting-export-to" type="date" className="border-white/10 bg-white/[0.08] text-slate-50" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
        </div>

        {/* Presets rapides */}
        <div className="flex flex-wrap gap-2">
          <Button data-testid="accounting-preset-this-month" size="sm" variant="outline" className="rounded-full border-white/10 bg-white/[0.05] text-slate-200 hover:bg-white/10" onClick={() => setPresetMonth(0)}>Mois en cours</Button>
          <Button data-testid="accounting-preset-prev-month" size="sm" variant="outline" className="rounded-full border-white/10 bg-white/[0.05] text-slate-200 hover:bg-white/10" onClick={() => setPresetMonth(-1)}>Mois précédent</Button>
          <Button data-testid="accounting-preset-year" size="sm" variant="outline" className="rounded-full border-white/10 bg-white/[0.05] text-slate-200 hover:bg-white/10" onClick={setPresetYear}>Année en cours</Button>
        </div>

        {/* Prévisualisation */}
        {preview && (
          <div data-testid="accounting-export-preview" className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-slate-400 text-xs">Factures</div>
              <div className="text-xl font-semibold text-slate-50" data-testid="accounting-preview-count">{preview.count}</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs">Total TTC</div>
              <div className="text-xl font-semibold text-slate-50" data-testid="accounting-preview-total">{preview.total_ttc.toFixed(2)} €</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs">Encaissé</div>
              <div className="text-xl font-semibold text-emerald-300" data-testid="accounting-preview-paid">{preview.paid_ttc.toFixed(2)} €</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs">Reste dû</div>
              <div className="text-xl font-semibold text-amber-300" data-testid="accounting-preview-unpaid">{preview.unpaid_ttc.toFixed(2)} €</div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2">
          <Button data-testid="accounting-export-csv-btn" disabled={busy || !preview?.count} onClick={() => download("csv")} className="rounded-full bg-emerald-500 text-white hover:bg-emerald-400">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Exporter CSV (Excel)
          </Button>
          <Button data-testid="accounting-export-pdf-btn" disabled={busy || !preview?.count} onClick={() => download("pdf")} variant="outline" className="rounded-full border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10">
            <FileText className="h-4 w-4 mr-2" />
            Exporter PDF récapitulatif
          </Button>
          {!preview?.count && (
            <span className="text-xs text-slate-400 self-center">Aucune facture sur cette période — sélectionnez une autre plage.</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
